import _ from 'underscore';
import { useMemo, useState } from 'react';

import DEFAULT_PROFILE_IMAGE from './media/default-profile-image.svg';

const DEBOUNCE_DELAY = 100;
const PREVIEW_PROPS = ['companyPageName', 'userProfileImageSrc', 'finalUrl', 'introductoryText', 'headline', 'callToAction'];

const getDefaultData = i18n => ({
  companyPageName: i18n.getString(_TL_('Company page')),
  userProfileImageSrc: DEFAULT_PROFILE_IMAGE,
  finalUrl: 'www.example.com',
  introductoryText: i18n.getString(_TL_('[Your Introductory Text Here]')),
  headline: i18n.getString(_TL_('[Your Headline Here]')),
  callToAction: '',
});

const getCurrentData = (adData, companyInfo) => ({
  companyPageName: _.get(companyInfo, ['Name']),
  userProfileImageSrc: _.get(companyInfo, ['LogoDownloadUrl']),
  finalUrl: _.get(adData, ['FinalUrls', 0]),
  introductoryText: _.get(adData, ['Descriptions', 'Description0']),
  imageList: _.get(adData, ['Images', 'ImageList']),
  headline: _.get(adData, ['Headlines', 'Headline0']),
  callToAction: _.get(adData, ['CallToAction', 'optionText']),
});

const getDataWithDefaults = (data, defaultData) =>
  PREVIEW_PROPS.reduce((acc, prop) => ({ ...acc, [prop]: data[prop] || defaultData[prop] }), {});

const hasAdPreviewDataChanged =
  (newAdData, adData) => (newAdData.length !== adData.length || !_.isMatch(newAdData, adData));

// As the ad previewer has lots of internal components, rerendering each time the ad data changes can be expensive.
// To avoid this, we debounce the ad data update to only update the ad previewer when the ad data has stopped changing.
export const useAdPreviewData = (i18n, adData, companyInfo) => {
  const defaultData = useMemo(() => getDefaultData(i18n), [i18n]);
  const currentData = useMemo(() => getCurrentData(adData, companyInfo), [adData, companyInfo]);

  const [data, setData] = useState(currentData);
  const debouncedSetData = useMemo(() => _.debounce(setData, DEBOUNCE_DELAY), []);

  if (hasAdPreviewDataChanged(currentData, data)) {
    debouncedSetData.cancel();
    debouncedSetData(currentData);
  }

  const dataWithDefaults = useMemo(() => getDataWithDefaults(data, defaultData), [data, defaultData]);

  return dataWithDefaults;
};
